import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BusinessInfoForm = ({ initialData, onSave, className = '' }) => {
  const [formData, setFormData] = useState({
    businessName: initialData?.businessName || 'تیم مرجان',
    businessType: initialData?.businessType || 'تیم مرجان',
    businessPhone: initialData?.businessPhone || '',
    businessEmail: initialData?.businessEmail || '',
    businessAddress:
      initialData?.businessAddress ||
      'الهیه،خزر شمالی،بالاتر از کوچه مرجان،پلاک۲۰',
    businessLicense: initialData?.businessLicense || '',
    taxId: initialData?.taxId || '',
    website: initialData?.website || '',
    instagram: initialData?.instagram || '',
    description: initialData?.description || '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.businessName?.trim()) {
      newErrors.businessName = 'نام کسب‌وکار الزامی است';
    }

    if (!formData?.businessPhone?.trim()) {
      newErrors.businessPhone = 'شماره تلفن کسب‌وکار الزامی است';
    }

    if (
      formData?.businessEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.businessEmail)
    ) {
      newErrors.businessEmail = 'فرمت ایمیل صحیح نیست';
    }

    if (!formData?.businessAddress?.trim()) {
      newErrors.businessAddress = 'آدرس کسب‌وکار الزامی است';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      onSave?.(formData);
    } catch (error) {
      console.error('Error saving business info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Building2" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              اطلاعات کسب‌وکار
            </h4>
            <p className="text-xs text-muted-foreground">
              این اطلاعات در پروفایل عمومی سالن شما و در فاکتورها نمایش داده
              می‌شود.
            </p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="نام کسب‌وکار"
            type="text"
            value={formData?.businessName}
            onChange={(e) =>
              handleInputChange('businessName', e?.target?.value)
            }
            error={errors?.businessName}
            required
            placeholder="نام سالن یا کسب‌وکار خود را وارد کنید"
          />

          <Input
            label="نوع کسب‌وکار"
            type="text"
            value={formData?.businessType}
            onChange={(e) =>
              handleInputChange('businessType', e?.target?.value)
            }
            error={errors?.businessType}
            placeholder="مثال: سالن زیبایی، آرایشگاه"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="شماره تلفن کسب‌وکار"
            type="tel"
            value={formData?.businessPhone}
            onChange={(e) =>
              handleInputChange('businessPhone', e?.target?.value)
            }
            error={errors?.businessPhone}
            required
            placeholder=""
          />

          <Input
            label="ایمیل کسب‌وکار"
            type="email"
            value={formData?.businessEmail}
            onChange={(e) =>
              handleInputChange('businessEmail', e?.target?.value)
            }
            error={errors?.businessEmail}
            placeholder=""
          />
        </div>

        <Input
          label="آدرس کسب‌وکار"
          type="text"
          value={formData?.businessAddress}
          onChange={(e) =>
            handleInputChange('businessAddress', e?.target?.value)
          }
          error={errors?.businessAddress}
          required
          placeholder="آدرس کامل سالن یا کسب‌وکار"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="شماره پروانه کسب"
            type="text"
            value={formData?.businessLicense}
            onChange={(e) =>
              handleInputChange('businessLicense', e?.target?.value)
            }
            error={errors?.businessLicense}
            placeholder="شماره پروانه کسب"
          />

          <Input
            label="شناسه مالیاتی"
            type="text"
            value={formData?.taxId}
            onChange={(e) => handleInputChange('taxId', e?.target?.value)}
            error={errors?.taxId}
            placeholder="شناسه مالیاتی"
          />
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Globe" size={16} />
            اطلاعات آنلاین
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="وب‌سایت"
              type="url"
              value={formData?.website}
              onChange={(e) => handleInputChange('website', e?.target?.value)}
              error={errors?.website}
              placeholder="www.example.com"
            />

            <Input
              label="اینستاگرام"
              type="text"
              value={formData?.instagram}
              onChange={(e) => handleInputChange('instagram', e?.target?.value)}
              error={errors?.instagram}
              placeholder="@username"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            توضیحات کسب‌وکار
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            rows={4}
            className="w-full p-3 border border-border rounded-md text-foreground bg-input focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="توضیح مختصری از کسب‌وکار و خدمات ارائه شده..."
          />
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <Button type="button" variant="outline" disabled={isLoading}>
            انصراف
          </Button>

          <Button
            type="submit"
            loading={isLoading}
            iconName="Save"
            iconPosition="right">
            ذخیره اطلاعات
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BusinessInfoForm;
